import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  MenuItem,
  Grid,
  Paper,
  Typography,
  Select,
  InputAdornment,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CustomAppBar from "../../components/AppBar";
import { useAlert } from "../../components/Alert";
import { getProcurementEstimatedPayout } from "../../utils/biomassProcurement";
import { getUserBasicDetailFromAuth } from "../../utils/common";
import {
  getCommodities,
  getLocationDetailsFromPincode,
} from "../../api/common";
import { createProcurement } from "../../api/biomassProcurement";
import { handleMobileKeyDown } from "../../globals/utils";

const ProcurementForm = () => {
  const { full_name, phone_number, user_code } = getUserBasicDetailFromAuth();
  const { showAlert } = useAlert();

  const [formData, setFormData] = useState({
    farmer: user_code,
    farmer_name: full_name,
    mobile: phone_number,
    crop: "",
    quantity_kg: "",
    acreage: "",
    estimated_payout: "",
    inspection_date: "",
    pincode: "",
    state: "",
    district: "",
    postoffice: "",
    village: "",
    address_line1: "",
    transportation_by: "FARMER",
    status: "PLACED",
  });
  const [crops, setCrops] = useState([]);
  const [postOffices, setPostOffices] = useState([]);
  const [isLocationLoading, setIsLocationLoading] = useState(false);

  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const handleChange = (e) => {
    if (e.target.name === "quantity_kg") {
      let estimatedPayout = getProcurementEstimatedPayout(
        e.target.value,
        formData.transportation_by
      );
      setFormData({
        ...formData,
        [e.target.name]: e.target.value,
        estimated_payout: estimatedPayout,
      });
      return;
    }
    if (e.target.name === "transportation_by") {
      let estimatedPayout = getProcurementEstimatedPayout(
        formData.quantity_kg,
        e.target.value
      );
      setFormData({
        ...formData,
        [e.target.name]: e.target.value,
        estimated_payout: estimatedPayout,
      });
      return;
    }
    if (e.target.name === "pincode") {
      setFormData({ ...formData, [e.target.name]: e.target.value });
      if (e.target.value.length !== 6) {
        setErrors({ ...errors, pincode: "Pincode must be 6 digits" });
        return;
      }
      setIsLocationLoading(true);
      getLocationDetailsFromPincode(e.target.value)
        .then((response) => {
          if (response) {
            console.log("");
            setFormData({
              ...formData,
              pincode: response.pincode,
              state: response.state,
              district: response.district,
              village: response.village,
            });
            setPostOffices(response.postOffices || []);
            setErrors({ ...errors, pincode: "", state: "", district: "" });
          } else {
            setErrors({ ...errors, pincode: "Invalid pincode" });
          }
        })
        .catch((error) => {
          console.error("Error fetching location details:", error);
          showAlert("error", "Failed to fetch location details");
        })
        .finally(() => {
          setIsLocationLoading(false);
        });
      return;
    }
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validate = () => {
    let tempErrors = {};
    if (!formData.farmer_name)
      tempErrors.farmer_name = "Farmer name is required";
    if (!formData.mobile.match(/^\d{10}$/))
      tempErrors.mobile = "Enter a valid 10-digit mobile number";
    if (!formData.crop) tempErrors.crop = "Crop name is required";
    if (formData.quantity_kg <= 0)
      tempErrors.quantity_kg = "Quantity must be greater than 0";
    if (formData.acreage <= 0)
      tempErrors.acreage = "Acreage must be greater than 0";
    if (formData.estimated_payout <= 0)
      tempErrors.estimated_payout = "Estimated payout must be greater than 0";
    if (!formData.inspection_date)
      tempErrors.inspection_date = "Inspection date is required";
    if (!formData.pincode.match(/^\d{6}$/))
      tempErrors.pincode = "Enter a valid 6-digit pincode";
    if (!formData.state) tempErrors.state = "State is required";
    if (!formData.district) tempErrors.district = "District is required";
    if (!formData.postoffice) tempErrors.postoffice = "Post office is required";
    if (!formData.village) tempErrors.village = "Village is required";
    if (!formData.address_line1)
      tempErrors.address_line1 = "Address is required";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log(formData);
    if (!validate()) return;
    try {
      const response = await createProcurement(formData);
      if (response) {
        showAlert("success", "Procurement created successfully");
        navigate("/procurement-list");
      }
    } catch (error) {
      console.error("Error submitting procurement:", error);
    }
  };

  const fetchCrops = async () => {
    try {
      let crops = await getCommodities("CROP");
      setCrops(crops || []);
    } catch (error) {
      console.error("Error fetching crops:", error);
      showAlert("error", "Failed to fetch crops");
    }
  };

  useEffect(() => {
    fetchCrops();
  }, []);

  return (
    <>
      <CustomAppBar />
      <Paper
        sx={{ padding: 3, maxWidth: 600, margin: "auto", paddingBottom: 10 }}
      >
        <Typography variant="h5" gutterBottom>
          Add New Procurement
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Farmer Name"
                name="farmer_name"
                value={formData.farmer_name}
                onChange={handleChange}
                required
                error={!!errors.farmer_name}
                helperText={errors.farmer_name}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                required
                type="tel"
                inputProps={{
                  maxLength: 10,
                  inputMode: "numeric",
                }}
                onKeyDown={handleMobileKeyDown}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">+91</InputAdornment>
                  ),
                }}
                error={!!errors.mobile}
                helperText={errors.mobile}
              />
            </Grid>
            <Grid item xs={12}>
              <Select
                fullWidth
                label="Crop Name"
                name="crop"
                value={formData.crop}
                onChange={handleChange}
                required
                error={!!errors.crop}
                helperText={errors.crop}
              >
                {crops.map((crop) => {
                  return (
                    <MenuItem key={crop.id} value={crop.id}>
                      {crop.name}
                    </MenuItem>
                  );
                })}
              </Select>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Quantity (kg)"
                name="quantity_kg"
                type="number"
                value={formData.quantity_kg}
                onChange={handleChange}
                required
                error={!!errors.quantity_kg}
                helperText={errors.quantity_kg}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Acreage"
                name="acreage"
                type="number"
                value={formData.acreage}
                onChange={handleChange}
                required
                error={!!errors.acreage}
                helperText={errors.acreage}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Inspection Date"
                name="inspection_date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.inspection_date}
                onChange={handleChange}
                required
                error={!!errors.inspection_date}
                helperText={errors.inspection_date}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Transportation By"
                name="transportation_by"
                value={formData.transportation_by}
                onChange={handleChange}
              >
                <MenuItem value="FARMER">Farmer</MenuItem>
                <MenuItem value="ASQI">ASQI</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Pincode"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                required
                error={!!errors.pincode}
                helperText={errors.pincode}
              />
            </Grid>
            {!isLocationLoading && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="State"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                    error={!!errors.state}
                    helperText={errors.state}
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="District"
                    name="district"
                    value={formData.district}
                    onChange={handleChange}
                    required
                    error={!!errors.district}
                    helperText={errors.district}
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Post Office"
                    name="postoffice"
                    value={formData.postoffice}
                    onChange={handleChange}
                  >
                    {postOffices.map((postOffice) => {
                      return (
                        <MenuItem key={postOffice} value={postOffice}>
                          {postOffice}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Village"
                name="village"
                value={formData.village}
                onChange={handleChange}
                required
                error={!!errors.village}
                helperText={errors.village}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                name="address_line1"
                value={formData.address_line1}
                onChange={handleChange}
                required
                error={!!errors.address_line1}
                helperText={errors.address_line1}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Estimated Payout"
                name="estimated_payout"
                type="number"
                value={formData.estimated_payout}
                required
                error={!!errors.estimated_payout}
                helperText={errors.estimated_payout}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
              >
                Submit
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </>
  );
};

export default ProcurementForm;
